<template>
	<div v-if="isUserAllowedInLevel1">
		<BaseHeader :title="title"></BaseHeader>
		<div class="main space-y-8 w-1/2">
			<div class="flex space-x-4">
				<BaseSelect
					:options="getSortedVariables"
					:disable-search="false"
					class="w-full"
					:label="$t('add_variables')"
					options-label="name"
					@input="insertVariable($event)"
				/>
				<BaseSelect
					:options="getSortedVariables"
					:disable-search="false"
					class="w-full"
					:label="$t('if_variable_then')"
					options-label="name"
					@input="insertIfVariable($event)"
				/>
			</div>

			<div class="flex-grow">
				<BaseTextarea
					ref="start_template"
					v-model="text_template.start_template"
					:label="$t('greeting')"
					text-area-class="h-16"
					@focus="focusTextarea('start_template')"
				/>

				<BaseTextarea
					ref="main_template"
					v-model="text_template.main_template"
					:required="true"
					:label="$t('mail_text')"
					text-area-class="h-48"
					@focus="focusTextarea('main_template')"
				/>

				<BaseTextarea
					ref="end_template"
					v-model="text_template.end_template"
					:label="$t('signature')"
					text-area-class="h-48"
					@focus="focusTextarea('end_template')"
				/>
			</div>
		</div>
		<Footer @backButtonClick="$router.go(-1)" class="mx-8" :is-continue-button="false">
			<template #right>
				<BaseButton @click="save()" class="btn-blue">{{ $t('general.save') }}</BaseButton>
			</template>
		</Footer>
	</div>
	<Forbidden v-else />
</template>

<script>
import RouteWatcher from '@/mixins/RouteWatcher';
import { TextTemplatesApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import BaseHeader from '@/components/navigation/BaseHeader';
import BaseTextarea from '@/components/forms/inputs/BaseTextarea';
import SaveButton from '@/components/forms/buttons/SaveButton';
import Validate from '@/mixins/Validate';
import BaseButton from '@/components/forms/buttons/BaseButton';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import Footer from '@/components/damagecases/Footer.vue';
import Authentication from '@/mixins/Authentication';
import Forbidden from '@/views/default/Forbidden.vue';

export default {
	name: 'TextTemplateEdit',
	components: { Forbidden, BaseSelect, BaseButton, SaveButton, BaseTextarea, BaseHeader, Footer },
	mixins: [RouteWatcher, Validate, Authentication],
	data() {
		return {
			text_template: {},
			title: this.$t('template'),
			activeTextareaRefKey: null,
		};
	},
	computed: {
		getSortedVariables() {
			return this.getVariables.map(x => x).sort((a, b) => a.name < b.name);
		},
		getVariables() {
			const notification_l2_damage_case_create_for_l3 = [
				{
					name: 'Unfallbeschreibung',
					key: 'unfall_beschreibung',
					templateKeys: ['notification_l2_damage_case_create_for_l3'],
				},
				{
					name: 'Fahrzeughalter',
					key: 'fahrzeughalter',
					templateKeys: ['notification_l2_damage_case_create_for_l3'],
				},
				{
					name: 'Hinweise an die Kanzlei',
					key: 'anwalt_hinweise',
					templateKeys: ['notification_l2_damage_case_create_for_l3'],
				},
				{
					name: 'Kunden E-Mail für Anwalt',
					key: 'kontakt_email_anwalt',
					templateKeys: ['notification_l2_damage_case_create_for_l3'],
				},
				{
					name: 'Kunde wünscht Kontakt per',
					key: 'kontakt_via',
					templateKeys: ['notification_l2_damage_case_create_for_l3'],
				},
			];
			const damageCaseVariables = [
				{
					name: this.$t('customer_lastname'),
					key: 'kunde_nachname',
				},
				{
					name: this.$t('customer_consultant_name'),
					key: 'kundenberater_name',
				},
				{
					name: this.$t('company_consultant'),
					key: 'kundenberater_firmen_name',
				},
				{
					name: this.$t('name_car_shop'),
					key: 'autohaus_name',
				},
				{
					name: this.$t('imd_connect_number'),
					key: 'imd_net',
				},
				{
					name: this.$t('consultant_phone_number'),
					key: 'kundenberater_telefonnummer',
				},
				{
					name: this.$t('car_shop_street'),
					key: 'autohaus_straße',
				},
				{
					name: this.$t('car_shop_city'),
					key: 'autohaus_stadt',
				},
				{
					name: this.$t('damage_assessor_name'),
					key: 'gutachter_name',
				},
				{
					name: this.$t('customer_name'),
					key: 'kunde_name',
				},
				{
					name: this.$t('customer_firstname'),
					key: 'kunde_vorname',
				},
				{
					name: this.$t('customer_company'),
					key: 'kunde_firma',
				},
				{
					name: this.$t('damage_case_creator_name'),
					key: 'schadenfall_ersteller_name',
				},
				{
					name: this.$t('damage_case_creator_company'),
					key: 'schadenfall_ersteller_firmen_name',
				},
				{
					name: this.$t('damage_case_creator_phone'),
					key: 'schadenfall_ersteller_telefonnummer',
				},
				{
					name: this.$t('license_plate'),
					key: 'kennzeichen',
				},
				{
					name: this.$t('accident_date'),
					key: 'unfall_datum',
				},
			].map(variable => {
				return {
					...variable,
					templateKeys: [
						'notification_l5_6_calculation',
						'notification_l5_6_calculation_pre_100',
						'notification_l5_6_calculation_pre_130',
						'notification_l5_6_calculation_pre_total',
						'create_repair_extension',
						'notification_l2_damage_case_create_for_l3',
						'notification_l2_damage_case_create',
						'notification_l5_6_damage_case_create',
						'damagecase_success',
						'notification_l2_assign_damage_assessor',
						'notification_damage_assessor_applied',
						'damage_case_payment_receipt_saved',
					],
				};
			});

			const publicVariables = [
				{
					name: this.$t('imd_connect_url'),
					key: 'url',
				},
				{
					name: this.$t('support_email_address'),
					key: 'support_email',
				},
				{
					name: this.$t('todays_date'),
					key: 'datum',
				},
				{
					name: this.$t('users_signature'),
					key: 'user_mail_signatur',
				},
				{
					name: this.$t('users_name'),
					key: 'user_name',
				},
				{
					name: this.$t('users_firstname'),
					key: 'user_vorname',
				},
				{
					name: this.$t('users_lastname'),
					key: 'user_nachname',
				},
				{
					name: this.$t('users_company'),
					key: 'user_firmen_name',
				},
				{
					name: this.$t('recipient_name'),
					key: 'empfänger_name',
				},
				{
					name: this.$t('recipient_lastname'),
					key: 'empfänger_nachname',
				},
				{
					name: this.$t('recipient_firstname'),
					key: 'empfänger_vorname',
				},
				{
					name: this.$t('users_company_address'),
					key: 'user_firmen_adresse',
				},
				{
					name: this.$t('password'),
					key: 'password',
				},
			];

			const variables = [
				...damageCaseVariables,
				...notification_l2_damage_case_create_for_l3,
				...publicVariables,
				{
					name: this.$t('company_id'),
					key: 'firmen_id',
					templateKeys: ['check_company_sign'],
				},
				{
					name: this.$t('company_name'),
					key: 'firmen_name',
					templateKeys: [
						'check_company_sign',
						'notification_l2_damage_case_create',
						'notification_l5_6_damage_case_create',
						'notification_damage_assessor_applied',
						'notification_lawyer_office_applied',
					],
				},
				{
					name: this.$t('sales_person_first_name'),
					key: 'sales_company_first_name',
					templateKeys: ['notification_accept_sales_registration_request'],
				},
				{
					name: this.$t('sales_person_last_name'),
					key: 'sales_company_last_name',
					templateKeys: ['notification_accept_sales_registration_request'],
				},
				{
					name: this.$t('owner_first_lastname'),
					key: 'firmen_inhaber_name',
					templateKeys: ['notification_damage_assessor_applied', 'notification_lawyer_office_applied'],
				},
				{
					name: this.$t('workshop_type'),
					key: 'autohaus_type',
					templateKeys: ['notification_damage_assessor_applied', 'notification_lawyer_office_applied'],
				},
				{
					name: this.$t('status'),
					key: 'status',
					templateKeys: ['notification_l5_6_repair_extension_status_update'],
				},
			];

			return variables.filter(variable => !variable.templateKeys || variable.templateKeys.includes(this.text_template.key));
		},
	},
	methods: {
		focusTextarea(refKey) {
			this.activeTextareaRefKey = refKey;
		},
		insertIfVariable(variable) {
			const secondPart = '{{' + variable.key + '}}{{endif}}';
			const wrapped_variable = '{{if:' + variable.key + '}}' + secondPart;

			if (this.activeTextareaRefKey) {
				this.$refs[this.activeTextareaRefKey].replaceAtSelected(wrapped_variable, wrapped_variable.length - secondPart.length);
			} else {
				this.$swal(
					this.$t('general.attention').toString(),
					this.$t('select_input_or_enter_value', { variable: wrapped_variable }).toString(),
					'warning',
				);
			}
		},
		insertVariable(variable) {
			const wrapped_variable = '{{' + variable.key + '}}';

			if (this.activeTextareaRefKey) {
				this.$refs[this.activeTextareaRefKey].replaceAtSelected(wrapped_variable);
			} else {
				this.$swal(
					this.$t('general.attention').toString(),
					this.$t('select_input_or_enter_value', { variable: wrapped_variable }).toString(),
					'warning',
				);
			}
		},
		save() {
			if (this.validate()) {
				TextTemplatesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
					.textTemplatesIdPut(this.$route.params.id, this.text_template)
					.then(response => {
						this.$store.commit('setConfirmLeaving', false);
						this.$swal(this.$t('general.success').toString(), this.$t('template_saved').toString(), 'success').then(_ => {
							this.$router.push({ name: 'text_templates' });
						});
					});
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
			}
		},
		onRouteChange() {
			if (this.isUserAllowedInLevel1) {
				this.requestTextTemplate();
			}
		},
		requestTextTemplate() {
			TextTemplatesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.textTemplatesIdGet(this.$route.params.id)
				.then(response => {
					const data = response.data.data;
					this.text_template = data;
					this.title = this.$t('template_input') + ' ' + data.name;
				});
		},
	},
};
</script>
